import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Code, AlertCircle } from "lucide-react";
import { isValidDeckCode } from "@/lib/utils/deck-code";

export function DeckImport() {
  const [deckCode, setDeckCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if there's an error from the deck builder
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clear the error from URL
      navigate("/deck-import", { replace: true });
    }
  }, [searchParams, navigate]);

  const handleImport = () => {
    setError(null);
    
    if (!deckCode.trim()) {
      setError("Please enter a deck code");
      return;
    }

    if (!isValidDeckCode(deckCode.trim())) {
      setError("Invalid deck code format");
      return;
    }
    
    // Navigate to deck builder with the code as a query parameter
    navigate(`/deck-builder?code=${encodeURIComponent(deckCode.trim())}`);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setDeckCode(text);
      setError(null);
    } catch (err) {
      setError("Failed to read from clipboard");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Import a Deck
          </CardTitle>
          <CardDescription>
            Paste your deck code in the text area below, and press Import. You will then be taken to view and edit the deck.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="deck-code">Deck Code</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePaste}
                className="h-8"
              >
                Paste from Clipboard
              </Button>
            </div>
            <Textarea
              id="deck-code"
              placeholder="Paste your deck code here..."
              value={deckCode}
              onChange={(e) => {
                setDeckCode(e.target.value);
                setError(null);
              }}
              className="min-h-[120px] font-mono text-sm"
            />
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-destructive">Import Error</p>
                  <p className="text-sm text-destructive/90">{error}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Please check that the deck code is correct and try again. If the problem persists, the deck code may be from a different game version or use incompatible unit/division IDs.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleImport}
              disabled={!deckCode.trim()}
              className="min-w-[120px]"
            >
              Import
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

